import { FC, useContext, useState } from "react";
import { Redirect } from "react-router-dom";
import { RegionSet } from "@app/region-sets";
import { RegionSetStatusType } from "@app/projects/enumerators";
import CreateProject from "@app/projects/create/createProject";
import { County } from "@app/geography";
import LookupRegionSet from "@app/region-sets/editor/lookupRegionSet";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ServiceAreaEditor } from "@app/region-sets/editor/ServiceAreaEditor";
import WizardStep from "@app/projects/create/WizardStep";
import { ServiceAreaEditorContext } from "@app/region-sets/editor/ServiceAreaEditorContext";
import NearbyCountiesPanelContent from "@app/region-sets/editor/NearbyCountiesPanelContent";
import { CountyStatus } from "@app/projects/create/countyStatus";

type CreateProjectStepServiceAreaProps = {
  completionTracker: boolean[];
  createProjectModel: CreateProject;
  baseUrl: string;
  handleCountyStatusUpdates: (countyStatuses: CountyStatus[]) => void;
  handleCountyStatusForCheckedState: (counties: County[]) => void;
  lookupRegionSet: LookupRegionSet;
  updateLookupRegionSet: (lookupRegionSet: LookupRegionSet) => void;
  onRegionSetChange: (regionSet: RegionSet) => void;
  onSubmitNearbyCounties: (updateCounties: County[], regionStatus: RegionSetStatusType) => void;
  progressList: string[];
  registerNearbyCountiesTriggerClose: (triggerCloser: () => void) => void;
  serviceAreaRegionSet: RegionSet;
  updateCounties: County[];
  currentIndex: number;
};

const CreateProjectStepServiceArea: FC<CreateProjectStepServiceAreaProps> = (props: CreateProjectStepServiceAreaProps) => {
  const [isNextDisabled, setIsNextDisabled] = useState(true);
  const [restartApp] = useState(!props.completionTracker.filter((_, index) => (props.currentIndex - 1) > index).every(step => step));
  const serviceAreaContext = useContext(ServiceAreaEditorContext);

  if (restartApp) {
    return <Redirect to={`${props.baseUrl}/1`} />;
  }

  const renderAddNearbyCounties = () => {
    const countySSAs = props.createProjectModel
      && props.createProjectModel.countyStatuses
      && props.createProjectModel.countyStatuses.map((countyStatus) => countyStatus.countySSA);

    if (countySSAs && countySSAs.length) {
      return (
        <FlyoutLink
          classNames="qes-button-secondary mr15 pe-2 ps-2"
          content={{
            body: (<NearbyCountiesPanelContent
              countySSAs={countySSAs}
              onSubmitNearbyCounties={props.onSubmitNearbyCounties}
            />),
            title: "Add Nearby Counties",
          }}
          testId="add-nearby-counties-flyout-link"
          size="20"
          onClose={() => props.registerNearbyCountiesTriggerClose(() => null)}
        >
          Add Nearby Counties
        </FlyoutLink>
      );
    }

    return null;
  }

  const handleRegionUpdate = (lookupRegionSet: LookupRegionSet) => {
    if (JSON.stringify(lookupRegionSet.children) !== "{}") {
      setIsNextDisabled(false);
    } else {
      setIsNextDisabled(true);
    }
    props.updateLookupRegionSet(lookupRegionSet);
  }

  const handleNextClick = () => {
    serviceAreaContext.handleComplete(props.onRegionSetChange);
  }

  const handlePrevClick = () => {
    if (!!props.lookupRegionSet 
      && JSON.stringify(props.lookupRegionSet) !== "{}"
      && Object.keys(props.lookupRegionSet?.children).length > 0) {
      serviceAreaContext.handleComplete(props.onRegionSetChange);
    }
  }

  return (
    <WizardStep
      {...props}
      actionButton={renderAddNearbyCounties}
      heading="Create a New Project"
      subheading="Define Analysis :: Service Area"
      baseUrl={props.baseUrl}
      prevUrl={(props.currentIndex - 1).toString()}
      nextUrl={(props.currentIndex + 1).toString()}
      progressList={props.progressList}
      onNextClick={handleNextClick}
      onPrevClick={handlePrevClick}
      onWizardStepsNavigation={handlePrevClick}
      isNextDisabled={isNextDisabled}
    >
      <ServiceAreaEditor
        countyStatuses={props.createProjectModel.countyStatuses}
        onComplete={props.onRegionSetChange}
        onUpdate={handleRegionUpdate}
        regionSet={props.serviceAreaRegionSet}
        handleCountyStatusUpdates={props.handleCountyStatusUpdates}
        updateCounties={props.updateCounties}
      />
    </WizardStep>
  );
}

export default CreateProjectStepServiceArea;
